import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import styles from './TablePage.module.css';

type LoginLog = {
  id: string;
  user_id: string | null;
  logged_in_at: string;
  device: string | null;
  user_email?: string;
};

const DEVICE_ICON: Record<string, string> = {
  ios: '🍎',
  android: '🤖',
  web: '🌐',
};

const PAGE_SIZE = 30;

export default function LoginLogsPage() {
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDevice, setFilterDevice] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    let query = supabase
      .from('login_logs')
      .select('*', { count: 'exact' })
      .order('logged_in_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (filterDevice !== 'all') {
      query = query.eq('device', filterDevice);
    }

    const { data, count } = await query;
    const logsData = data ?? [];

    // Fetch user emails separately
    const userIds = Array.from(new Set(logsData.map(l => l.user_id).filter(Boolean)));
    let emailMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, email')
        .in('id', userIds);
      (users ?? []).forEach(u => { emailMap[u.id] = u.email; });
    }

    setLogs(logsData.map(l => ({ ...l, user_email: l.user_id ? emailMap[l.user_id] : undefined })));
    setTotal(count ?? 0);
    setLoading(false);
  }, [page, filterDevice]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Reset page when filter changes
  useEffect(() => { setPage(0); }, [filterDevice]);

  const todayCount = logs.filter(l => {
    const d = new Date(l.logged_in_at);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  }).length;

  const devices = ['all', 'ios', 'android', 'web'];

  if (loading && logs.length === 0) return <div className={styles.loading}>로딩 중...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>🔐 로그인 이력</h1>
          <span className={styles.count}>전체 {total.toLocaleString()}건 · 현재 페이지 오늘 {todayCount}건</span>
          <div className={styles.dbBadge}>🗄 login_logs</div>
        </div>
        <div className={styles.actions}>
          <button className="btn btn-ghost" onClick={fetchLogs}>↺</button>
        </div>
      </div>

      {/* Device filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {devices.map(d => (
          <button
            key={d}
            onClick={() => setFilterDevice(d)}
            style={{
              padding: '6px 16px',
              borderRadius: 999,
              border: 'none',
              background: filterDevice === d ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)',
              color: filterDevice === d ? 'white' : 'rgba(255,255,255,0.5)',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {d === 'all' ? '전체' : `${DEVICE_ICON[d] ?? ''} ${d}`}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>사용자</th>
              <th>기기</th>
              <th>로그인 시각</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)' }}>기록 없음</td></tr>
            ) : logs.map((log, idx) => (
              <tr key={log.id}>
                <td style={{ color: 'rgba(255,255,255,0.3)', width: 50 }}>{page * PAGE_SIZE + idx + 1}</td>
                <td style={{ maxWidth: 220 }}>
                  <div style={{ fontSize: 13, color: 'white', fontWeight: 600 }}>{log.user_email ?? '—'}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{log.user_id ?? '—'}</div>
                </td>
                <td>
                  <span style={{
                    padding: '3px 10px',
                    borderRadius: 999,
                    background: log.device === 'web' ? 'rgba(100,160,255,0.18)' : log.device === 'ios' ? 'rgba(255,200,100,0.18)' : 'rgba(100,220,150,0.18)',
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    {DEVICE_ICON[log.device ?? ''] ?? '❓'} {log.device ?? '—'}
                  </span>
                </td>
                <td style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, whiteSpace: 'nowrap' }}>
                  {new Date(log.logged_in_at).toLocaleString('ko-KR', {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 20 }}>
          <button className="btn btn-ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← 이전</button>
          <span style={{ padding: '8px 16px', color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
            {page + 1} / {Math.ceil(total / PAGE_SIZE)}
          </span>
          <button className="btn btn-ghost" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>다음 →</button>
        </div>
      )}
    </div>
  );
}
